import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EntityEditForm } from "@/components/common/EntityEditForm";
import { useEntityEditForm } from "@/hooks/common/useEntityEditForm";
import { useTranslation } from "react-i18next";
import { CandidateCustomFieldsEditTab } from "./CandidateCustomFieldsEditTab";
import { SkillsEditor } from "./SkillsEditor";
import { LanguageManager } from "@/components/ui/LanguageManager";
import { CandidateLanguage } from "@/types/candidate";

interface CandidateEditTabsProps {
  candidate: any;
  isEditing: boolean;
  onChange: (field: string, value: string | string[]) => void;
  customFieldsConfig?: any[];
  languages: CandidateLanguage[];
  onLanguagesChange: (languages: any[]) => void;
  languagesLoading?: boolean;
}

export const CandidateEditTabs: React.FC<CandidateEditTabsProps> = ({ 
  candidate, 
  isEditing, 
  onChange,
  customFieldsConfig = [],
  languages,
  onLanguagesChange,
  languagesLoading = false
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('basic');

  const handleFieldChange = (field: string, value: string | string[]) => {
    onChange(field, value);
  };

  const handleSkillsChange = (skills: string[]) => {
    onChange('skills', skills);
  };

  const languageEntries = languages.map(lang => ({
    id: lang.id,
    language: lang.language,
    language_code: lang.language_code,
    level: lang.cefr_level,
    is_user_owned: lang.is_user_owned
  }));

  // Create separate form sections for Basic Info tab (excluding skills)
  const basicFormProps = useEntityEditForm({
    entityType: 'candidate',
    entity: candidate,
    onChange: handleFieldChange,
    tabType: 'basic'
  });

  const contactFormProps = useEntityEditForm({
    entityType: 'candidate',
    entity: candidate,
    onChange: handleFieldChange,
    tabType: 'contact'
  });

  const professionalFormProps = useEntityEditForm({
    entityType: 'candidate',
    entity: candidate,
    onChange: handleFieldChange,
    tabType: 'professional'
  });

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full flex flex-wrap gap-1 bg-muted rounded-none p-1">
          <TabsTrigger value="basic" className="flex-shrink-0 text-xs sm:text-sm">
            {t('candidates.editTabs.basicInfo', 'Basic Info')}
          </TabsTrigger>
          <TabsTrigger value="skillsLanguages" className="flex-shrink-0 text-xs sm:text-sm">
            {t('candidates.tabs.skillsLanguages', 'Skills & Languages')}
          </TabsTrigger>
          {customFieldsConfig.length > 0 && (
            <TabsTrigger value="customFields" className="flex-shrink-0 text-xs sm:text-sm">
              {t('candidates.customFields.title', 'Custom')}
            </TabsTrigger>
          )}
        </TabsList>
        
        <TabsContent value="basic" className="mt-6">
          <div className="space-y-6">
            <EntityEditForm
              {...basicFormProps}
              isEditing={isEditing}
              title=""
              showSkillsAtBottom={false}
              entity={candidate}
            />
            
            <EntityEditForm
              {...contactFormProps}
              isEditing={isEditing}
              title=""
              showSkillsAtBottom={false}
              entity={candidate}
            />
            
            <EntityEditForm
              {...professionalFormProps}
              isEditing={isEditing}
              title=""
              showSkillsAtBottom={false}
              entity={candidate}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="skillsLanguages" className="mt-6 space-y-6">
          <div className="space-y-4">
            <SkillsEditor 
              skills={candidate.skills || []} 
              onChange={handleSkillsChange} 
              label={t("candidates.newCandidateModal.skillsLabel", "Skills")}
              placeholder={t("candidates.newCandidateModal.skillsPlaceholder", "Add a skill")}
              addButtonText={t("candidates.newCandidateModal.addSkillButton", "Add")}
            />
            
            <LanguageManager
              languages={languageEntries}
              onChange={onLanguagesChange}
              type="candidate"
              disabled={languagesLoading || !isEditing}
            />
          </div>
        </TabsContent>
        
        {customFieldsConfig.length > 0 && (
          <TabsContent value="customFields" className="mt-6">
            <CandidateCustomFieldsEditTab
              candidate={candidate}
              onChange={handleFieldChange}
              isEditing={isEditing}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}; 